import { effectPlugins, effects } from '@akr/animate-effects';
import { ensureGsap } from '@akr/animate-gsap';

/** @type {Map<Element, IntersectionObserver>} */
const registry = new Map();

/** @param {HTMLElement} el */
function parseOptions(el) {
  const {
    animate,
    animateDelay,
    animateDuration,
    animateStagger,
    animateThreshold,
    animateOffset,
    animateOnce,
    animateOnPortrait,
    animateDirection,
    animateStrength,
    animateSpeed,
  } = el.dataset;

  return {
    name: /** @type {keyof typeof effects} */ (animate),
    delay: parseFloat(animateDelay ?? '0'),
    duration: parseFloat(animateDuration ?? '1'),
    stagger: parseFloat(animateStagger ?? '1'),
    threshold: parseFloat(animateThreshold ?? '0'),
    offset: parseFloat(animateOffset ?? '0'),
    once: animateOnce !== 'false',
    onPortrait: animateOnPortrait !== 'false',
    direction: animateDirection ?? 'forward',
    strength: parseFloat(animateStrength ?? '0.15'),
    speed: parseFloat(animateSpeed ?? '100'),
  };
}

/**
 * Registers an element for scroll-triggered animation. GSAP (and any plugin the
 * effect needs, e.g. SplitText) is only fetched once the element nears the
 * viewport — not on page load, and not just because the component exists.
 * @param {Element} el
 */
export function registerAnimate(el) {
  if (!(el instanceof HTMLElement) || registry.has(el)) return;

  const options = parseOptions(el);
  if (!effects[options.name]) return;

  if (!options.onPortrait && matchMedia('(orientation: portrait)').matches) {
    el.classList.add('--animate-ready');
    return;
  }

  const observer = new IntersectionObserver(
    async ([entry]) => {
      if (!entry.isIntersecting) {
        el.dispatchEvent(new CustomEvent('animate:leave', { bubbles: true }));
        return;
      }

      el.dispatchEvent(new CustomEvent('animate:enter', { bubbles: true }));

      await ensureGsap(effectPlugins[options.name] ?? []);
      await document.fonts.ready;
      if (!el.isConnected) return;

      const tl = effects[options.name](el, options);
      if (!tl) return;

      el.classList.add('--animate-ready');
      tl.play(0);

      if (options.once) unregisterAnimate(el);
    },
    {
      rootMargin: `${options.offset}px 0px -${options.offset}px 0px`,
      threshold: options.threshold,
    },
  );

  registry.set(el, observer);
  observer.observe(el);
}

/** @param {Element} el */
export function unregisterAnimate(el) {
  registry.get(el)?.disconnect();
  registry.delete(el);
}

/** @param {ParentNode} [root] */
export function scanAnimate(root = document) {
  root.querySelectorAll('[data-animate]').forEach(registerAnimate);
}

/** @param {ParentNode} [root] */
export function cleanupAnimate(root = document) {
  root.querySelectorAll('[data-animate]').forEach(unregisterAnimate);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => scanAnimate());
} else {
  scanAnimate();
}

// Catches any [data-animate] element inserted after initial load, regardless of
// source — Section Rendering API morphs, cart drawer, predictive search, quick add...
new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (!(node instanceof Element)) continue;
      if (node.matches('[data-animate]')) registerAnimate(node);
      node.querySelectorAll('[data-animate]').forEach(registerAnimate);
    }
    for (const node of mutation.removedNodes) {
      if (!(node instanceof Element)) continue;
      if (node.matches('[data-animate]')) unregisterAnimate(node);
      node.querySelectorAll('[data-animate]').forEach(unregisterAnimate);
    }
  }
}).observe(document.documentElement, { childList: true, subtree: true });
